import { useState, useEffect, useCallback } from "react";
import { X, Share } from "lucide-react";
import { Button } from "@/components/ui/button";

const DISMISS_KEY = "gerio_pwa_install_dismissed_at";
const DISMISS_DAYS = 7;

function isStandalone() {
  return (
    window.matchMedia("(display-mode: standalone)").matches ||
    (navigator as any).standalone === true
  );
}

function isIos() {
  return /iphone|ipad|ipod/i.test(navigator.userAgent);
}

function wasDismissedRecently(): boolean {
  const raw = localStorage.getItem(DISMISS_KEY);
  if (!raw) return false;
  const diff = Date.now() - Number(raw);
  return diff < DISMISS_DAYS * 24 * 60 * 60 * 1000;
}

export function PwaInstallBanner() {
  const [visible, setVisible] = useState(false);
  const [showIos, setShowIos] = useState(false);

  useEffect(() => {
    if (isStandalone() || wasDismissedRecently()) return;

    if (isIos()) {
      setShowIos(true);
      setVisible(true);
      return;
    }

    // Check if prompt was already captured globally
    if ((window as any).__pwaInstallPrompt) {
      setVisible(true);
      return;
    }

    const handler = () => setVisible(true);
    window.addEventListener("beforeinstallprompt", handler);
    return () => window.removeEventListener("beforeinstallprompt", handler);
  }, []);

  const dismiss = useCallback(() => {
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
    setVisible(false);
  }, []);

  const install = useCallback(async () => {
    const prompt = (window as any).__pwaInstallPrompt;
    if (!prompt) return;
    prompt.prompt();
    await prompt.userChoice;
    (window as any).__pwaInstallPrompt = null;
    setVisible(false);
  }, []);

  if (!visible) return null;

  return (
    <div className="fixed bottom-0 inset-x-0 z-[9999] safe-area-bottom animate-in slide-in-from-bottom-4 duration-300">
      <div className="mx-3 mb-3 rounded-2xl border border-border bg-card shadow-lg p-4 flex items-center gap-3">
        <img
          src="/pwa-icon-192.png"
          alt="Gerio"
          className="h-11 w-11 rounded-xl shrink-0"
        />

        <div className="flex-1 min-w-0">
          {showIos ? (
            <p className="text-sm text-foreground leading-snug">
              Para instalar: toca em{" "}
              <Share className="inline h-4 w-4 -mt-0.5 text-primary" />{" "}
              partilhar e depois{" "}
              <span className="font-semibold">"Adicionar ao ecrã inicial"</span>
            </p>
          ) : (
            <p className="text-sm text-foreground leading-snug">
              Instala o Gerio no teu telemóvel para uma melhor experiência
            </p>
          )}
        </div>

        {!showIos && (
          <Button size="sm" onClick={install} className="shrink-0 rounded-full text-xs px-4">
            Instalar app
          </Button>
        )}

        <button
          onClick={dismiss}
          aria-label="Fechar"
          className="shrink-0 p-1.5 rounded-full text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}
